import clsx from "clsx";
import type { JSX } from "react";
import React from "react";

import styles from "./styles.module.scss";
import type { IButtonSize } from "./types";

import { Icon } from "@/components/atoms/icons";

export interface IPaginationLinkProps extends React.ComponentProps<"a"> {
  isActive?: boolean;
  size?: IButtonSize;
  disabled?: boolean;
}

export function Pagination({ ...props }: React.ComponentProps<"nav">) {
  return (
    <nav
      role={"navigation"}
      aria-label={"pagination"}
      className={styles.paginationContainer}
      {...props}
    />
  );
}
Pagination.displayName = "Pagination";

export const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ ...props }, ref) => (
  <ul ref={ref} className={styles.paginationContent} {...props} />
));
PaginationContent.displayName = "PaginationContent";

interface IPaginationItemProps extends React.ComponentProps<"li"> {
  isActive?: boolean;
}

export const PaginationItem = React.forwardRef<
  HTMLLIElement,
  IPaginationItemProps
>(({ isActive, ...props }, ref) => (
  <li
    ref={ref}
    className={clsx(
      styles.paginationItem,
      isActive && styles.paginationItemActive,
    )}
    {...props}
  />
));
PaginationItem.displayName = "PaginationItem";

export function PaginationLink({
  className,
  isActive,
  ...props
}: IPaginationLinkProps): JSX.Element {
  return (
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    <a
      aria-current={isActive ? "page" : undefined}
      className={clsx(className, props.disabled && styles.disabled)}
      {...props}
    />
  );
}
PaginationLink.displayName = "PaginationLink";

interface IPaginationActionProps
  extends React.ComponentProps<typeof PaginationLink> {
  disabled?: boolean;
}

export function PaginationPrevious({
  disabled,
  ...props
}: IPaginationActionProps) {
  return (
    <PaginationLink
      aria-label={"Go to previous page"}
      size={"default"}
      className={clsx(styles.prevButton)}
      {...props}
      disabled={disabled}
    >
      <Icon name={"AltArrowRight"} type={"linear"} />
      {props.children}
    </PaginationLink>
  );
}
PaginationPrevious.displayName = "PaginationPrevious";

export function PaginationNext({ disabled, ...props }: IPaginationActionProps) {
  return (
    <PaginationLink
      size={"default"}
      className={clsx(styles.nextButton)}
      {...props}
      disabled={disabled}
    >
      {props.children}
      <Icon name={"AltArrowLeft"} type={"linear"} />
    </PaginationLink>
  );
}
PaginationNext.displayName = "PaginationNext";

export function PaginationEllipsis({ ...props }: React.ComponentProps<"span">) {
  return (
    <span aria-hidden className={clsx(styles.paginationEllipsis)} {...props}>
      <Icon name={"MenuDots"} type={"linear"} />
      <span className={"sr-only"}>{"More pages"}</span>
    </span>
  );
}
PaginationEllipsis.displayName = "PaginationEllipsis";
